import { deleteField, Timestamp } from 'firebase/firestore';
import { parseMlvProductIdFromUrlOrRaw } from '../../../core/navigation/mlvDeepLink.js';
import { escapeHtml as esc } from '../../../core/ui/escapeHtml.js';
import {
  parseMontoAbonoComisionAdmin,
  parseMontoAdminNoNegativo,
  parseVigenciaDiasPublicidadHome,
} from '../domain/adminRules.js';
import {
  slotPublicidadCatalogoVigente,
  validoHastaSlotMs,
} from '../../comprador/domain/publicidadHomeRules.js';

const RX_UID = /^[A-Za-z0-9_-]{10,128}$/;

function debounce(fn, ms) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}

function fmtArs(n) {
  const x = Number(n) || 0;
  return x.toLocaleString('es-AR', { maximumFractionDigits: 2 });
}

function htmlEstadoCuentaAdmin(row) {
  const deudaRaw = Number(row?.comision_deuda_ars) || 0;
  const saldoRaw = Number(row?.saldo_favor_ars) || 0;
  const net = Math.round((deudaRaw - saldoRaw) * 100) / 100;
  const deuda = net > 0 ? net : 0;
  const saldo = net < 0 ? Math.round(-net * 100) / 100 : 0;
  const imp = Number(row?.comision_ventas_impagas_count) || 0;
  let situacion = '';
  if (deuda <= 0 && imp <= 0) {
    situacion =
      'La cuenta está <strong class="text-emerald-800">al día</strong> respecto de comisiones: no hay deuda registrada ni ventas marcadas como impagas en el contador.';
  } else if (deuda > 0) {
    situacion = `Hay <strong class="text-amber-900">deuda neta de comisión</strong> (ya descontado el saldo a favor registrado). Lo imputado por MP o manual se aplica a este saldo; el excedente queda como crédito.`;
    if (imp > 0) {
      situacion += ` Hay <strong>${imp}</strong> venta(s) con comisión aún no regularizada según el contador interno.`;
    }
  } else {
    situacion =
      'No hay deuda neta pero el contador de ventas impagas es mayor a cero; conviene revisar o imputar pagos para alinear.';
  }
  if (saldo > 0) {
    situacion +=
      ' <span class="text-slate-700">Crédito a favor: se aplicará automáticamente al generar nueva comisión.</span>';
  }
  return `
    <div class="rounded-xl border border-slate-200/90 bg-white p-3 shadow-sm">
      <p class="text-xs font-semibold uppercase tracking-wide text-slate-500">Estado de cuenta</p>
      <div class="mt-3 grid gap-2 sm:grid-cols-3">
        <div class="rounded-lg bg-slate-50 px-3 py-2">
          <p class="text-[10px] font-medium uppercase text-slate-500">Deuda comisión</p>
          <p class="text-lg font-bold text-slate-900">$ ${esc(fmtArs(deuda))}</p>
        </div>
        <div class="rounded-lg bg-slate-50 px-3 py-2">
          <p class="text-[10px] font-medium uppercase text-slate-500">Saldo a favor</p>
          <p class="text-lg font-bold text-brand-primary">$ ${esc(fmtArs(saldo))}</p>
        </div>
        <div class="rounded-lg bg-slate-50 px-3 py-2">
          <p class="text-[10px] font-medium uppercase text-slate-500">Ventas impagas (nº)</p>
          <p class="text-lg font-bold text-slate-900">${esc(String(imp))}</p>
        </div>
      </div>
      <p class="mt-3 text-xs leading-relaxed text-slate-600">${situacion}</p>
    </div>
    <div class="rounded-xl border border-brand-primary/25 bg-white p-3 shadow-sm">
      <label class="block text-xs font-medium text-slate-700">Monto recibido (ARS)</label>
      <p class="mt-0.5 text-[11px] text-slate-500">Imputá lo que ingresó por MP, transferencia u otro medio. Se descuenta de la deuda; el excedente pasa a saldo a favor.</p>
      <div class="mt-2 flex flex-col gap-2 sm:flex-row sm:items-end">
        <input id="admin-acc-monto-recibido" type="text" inputmode="decimal" autocomplete="off" placeholder="Ej. 15000" class="w-full flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
        <button type="button" id="admin-acc-save-recibido" class="w-full shrink-0 rounded-xl bg-brand-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:brightness-95 sm:w-auto">Guardar imputación</button>
      </div>
    </div>
    <details class="rounded-lg border border-slate-200 bg-slate-50/80 p-3 text-sm">
      <summary class="cursor-pointer font-medium text-slate-700">Ajuste directo de deuda y saldo</summary>
      <p class="mt-2 text-[11px] text-slate-500">Solo si necesitás corregir números a mano (no registra un “pago” por monto recibido).</p>
      <div class="mt-3 grid gap-3 sm:grid-cols-2">
        <div>
          <label class="block text-xs font-medium text-slate-600">Deuda comisión (ARS)</label>
          <input id="admin-acc-deuda" type="text" inputmode="decimal" class="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" />
        </div>
        <div>
          <label class="block text-xs font-medium text-slate-600">Saldo a favor (ARS)</label>
          <input id="admin-acc-saldo" type="text" inputmode="decimal" class="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" />
        </div>
      </div>
      <button type="button" id="admin-acc-save-montos" class="mt-3 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50">Guardar ajuste directo</button>
    </details>`;
}

export function createAdminPresenter({ render, adminRepository }) {
  async function showPanelAdmin({ onBack }) {
    /** @type {null | Awaited<ReturnType<typeof adminRepository.obtenerCuentaPorPersonaId>>} */
    let selected = null;
    /** @type {(null | { titulo: string, subtitulo?: string|null, vendedor_uid: string, imagen_url?: string|null, producto_id?: string, valido_hasta?: unknown, vigencia_dias?: number })[]} */
    let destacadosSlots = [null, null];
    /** @type {object[]} */
    let comerciosRows = [];
    let pubDocSnapshot = null;

    render(`
      <main class="min-h-screen max-w-5xl mx-auto space-y-8 bg-brand-surface p-4 sm:p-6">
        <header class="flex flex-wrap items-center justify-between gap-3">
          <h1 class="text-xl font-bold text-slate-900">Panel Admin</h1>
          <button id="admin-back" type="button" class="rounded-lg border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50">Volver</button>
        </header>

        <p id="admin-msg-global" class="min-h-[1.25rem] text-sm text-slate-600"></p>

        <section class="rounded-2xl border border-violet-200 bg-violet-50/50 p-4 shadow-sm sm:p-5">
          <div class="flex flex-wrap items-center justify-between gap-2">
            <h2 class="font-heading text-lg font-bold text-slate-900">Moderación — alertas</h2>
            <button type="button" id="admin-inbox-refresh" class="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-800 hover:bg-slate-50">Actualizar</button>
          </div>
          <p class="mt-1 text-xs text-slate-600">Cuando un vendedor valida GPS dentro de La Victoria, se listan acá los códigos vecino que habían denunciado (únicos por etiqueta).</p>
          <ul id="admin-inbox-list" class="mt-3 space-y-2 text-sm"></ul>
        </section>

        <section class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
          <h2 class="font-heading text-lg font-bold text-slate-900">Cuenta por email</h2>
          <p class="mt-1 text-sm text-slate-600">Buscá por email; ves el estado de comisiones, imputás el monto recibido y, si hace falta, el plan.</p>
          <div class="relative mt-4">
            <label class="sr-only" for="admin-search-uid">Buscar por email</label>
            <input id="admin-search-uid" type="search" inputmode="email" autocomplete="off" placeholder="Escribí el email del vecino…" class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-primary/30" />
            <ul id="admin-uid-suggest" class="absolute z-30 mt-1 hidden max-h-60 w-full overflow-auto rounded-xl border border-slate-200 bg-white py-1 shadow-lg" role="listbox"></ul>
          </div>

          <div id="admin-account-panel" class="mt-5 hidden space-y-4 rounded-xl border border-brand-primary/20 bg-brand-primary/5 p-4">
            <div>
              <p class="text-xs font-semibold uppercase tracking-wide text-slate-500">Seleccionado</p>
              <p id="admin-acc-nombre" class="text-lg font-bold text-slate-900"></p>
              <p id="admin-acc-uid" class="font-mono text-xs text-slate-600"></p>
            </div>
            <div id="admin-acc-estado-wrap" class="space-y-3"></div>
            <div class="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
              <p class="text-xs font-semibold uppercase tracking-wide text-slate-500">Acceso a la app</p>
              <p class="mt-1 text-[11px] leading-relaxed text-slate-600">
                <strong class="text-slate-800">Suspendida</strong> bloquea el uso del usuario. <strong class="text-slate-800">Habilitada</strong> restaura el acceso.
              </p>
              <div class="mt-2 flex flex-wrap items-end gap-2">
                <select id="admin-acc-estado-cuenta" class="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm">
                  <option value="activa">Habilitada</option>
                  <option value="suspendida">Suspendida</option>
                </select>
                <button type="button" id="admin-acc-guardar-estado" class="rounded-lg bg-slate-800 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:brightness-95">
                  Guardar acceso
                </button>
              </div>
            </div>
            <div class="border-t border-slate-200/80 pt-4">
              <label class="block text-xs font-medium text-slate-600">Tipo de cuenta</label>
              <select id="admin-acc-plan" class="mt-2 w-full max-w-xs rounded-lg border border-slate-300 px-3 py-2 text-sm">
                <option value="free">Free</option>
                <option value="userPremium">User Premium</option>
                <option value="marketPremium">Market Premium</option>
              </select>
              <p class="mt-2 text-[11px] text-slate-500">Al elegir Premium se fija <strong class="font-semibold text-slate-700">30 días</strong> de vigencia desde ahora; al vencer, una tarea en servidor baja a Free.</p>
              <p id="admin-acc-premium-hasta" class="mt-2 text-[11px] leading-relaxed text-slate-600"></p>
            </div>
            <div id="admin-acc-servicios-block" class="border-t border-slate-200/80 pt-4 space-y-3">
              <p class="text-xs font-semibold uppercase tracking-wide text-slate-500">Servicios del barrio</p>
              <p class="text-[11px] leading-relaxed text-slate-500">
                Independiente del plan de catálogo (User/Mark Premium). <strong class="text-slate-700">Verificado</strong> es la insignia de identidad en fichas. <strong class="text-slate-700">Tarjetas disponibles</strong> es cuántas publicaciones antes/después puede crear.
              </p>
              <div class="flex flex-wrap items-end gap-3">
                <div>
                  <label class="block text-xs font-medium text-slate-600" for="admin-serv-prof-modo">Estado profesional</label>
                  <select id="admin-serv-prof-modo" class="mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm">
                    <option value="free">Free (no verificado)</option>
                    <option value="verificado">Verificado</option>
                  </select>
                </div>
                <div>
                  <label class="block text-xs font-medium text-slate-600" for="admin-serv-saldo">Tarjetas disponibles</label>
                  <input id="admin-serv-saldo" type="number" min="0" max="99" step="1" inputmode="numeric" class="mt-1 w-24 rounded-lg border border-slate-300 px-3 py-2 text-sm" value="0" />
                </div>
                <button type="button" id="admin-serv-guardar-perfil" class="rounded-lg bg-slate-800 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:brightness-95">
                  Guardar perfil servicios
                </button>
              </div>
              <div id="admin-serv-tarjetas-host" class="space-y-2"></div>
            </div>
          </div>
        </section>

        <section class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
          <h2 class="font-heading text-lg font-bold text-slate-900">Publicidad — inicio catálogo</h2>
          <p id="admin-msg-pub" class="mt-2 min-h-[1.25rem] text-sm text-slate-600"></p>
          <p class="text-[11px] leading-relaxed text-slate-500">Cada espacio tiene sus propios días de visibilidad en el catálogo. La identidad del vendedor se referencia por <strong>UID</strong> o <strong>código vecino</strong> (4 caracteres del WhatsApp).</p>

          <div class="mt-6 space-y-2 rounded-xl border border-amber-200/80 bg-amber-50/40 p-4">
            <div class="flex flex-wrap items-center justify-between gap-2">
              <h3 class="font-semibold text-amber-950">Negocio principal (negocio del día)</h3>
              <span id="pub-badge-negocio" class="shrink-0 rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-slate-600">—</span>
            </div>
            <p class="text-xs text-amber-900/80">Título, subtítulo, identificador del vendedor (UID o código vecino) y días en el catálogo. Usá <strong class="font-semibold">Verificar</strong> para confirmar que la cuenta existe.</p>
            <div class="mt-3 grid gap-3 sm:grid-cols-2">
              <div class="sm:col-span-2">
                <label class="text-xs font-medium text-slate-600">Título</label>
                <input id="pub-neg-titulo" type="text" class="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
              </div>
              <div class="sm:col-span-2">
                <label class="text-xs font-medium text-slate-600">Subtítulo</label>
                <input id="pub-neg-sub" type="text" class="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
              </div>
              <div class="sm:col-span-2">
                <label class="text-xs font-medium text-slate-600">UID Firebase o código vecino (4 chars)</label>
                <div class="mt-1 flex items-stretch gap-2">
                  <input id="pub-neg-uid" type="text" autocomplete="off" class="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="UID largo o tag de 4 chars" />
                  <button type="button" id="pub-neg-uid-verificar" class="shrink-0 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-800 shadow-sm hover:bg-slate-50">
                    Verificar
                  </button>
                </div>
                <p id="pub-neg-uid-hint" class="mt-1.5 min-h-[2.75rem] text-[11px] leading-snug text-slate-400" aria-live="polite"></p>
              </div>
              <div>
                <label class="text-xs font-medium text-slate-600">Días en el catálogo (1–365)</label>
                <input id="pub-neg-dias" type="number" min="1" max="365" step="1" inputmode="numeric" class="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value="7" />
              </div>
              <div class="sm:col-span-2">
                <label class="text-xs font-medium text-slate-600">URL imagen de fondo (opcional)</label>
                <input id="pub-neg-img" type="url" class="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="https://…" />
              </div>
            </div>
            <div class="mt-4 border-t border-amber-200/60 pt-3">
              <button type="button" id="pub-btn-publicar-negocio" class="rounded-xl bg-brand-primary px-5 py-2.5 text-sm font-bold text-white shadow-md transition hover:brightness-95">
                Publicar negocio principal
              </button>
              <p class="mt-1.5 text-[11px] text-amber-900/75">Solo actualiza el cartel principal; no modifica destacados ni comercios.</p>
            </div>
          </div>

          <div class="mt-6 space-y-3 rounded-xl border border-slate-200 bg-slate-50/80 p-4">
            <div class="flex flex-wrap items-center justify-between gap-2">
              <h3 class="font-semibold text-slate-900">Destacados del barrio</h3>
              <span id="pub-badge-dest" class="shrink-0 rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-slate-600">—</span>
            </div>
            <p class="text-xs text-slate-600">Hasta 2 tarjetas en el inicio del catálogo. Pegá la URL o el ID de un producto ya publicado; los datos de la card salen del producto y del perfil del vendedor.</p>
            <ul id="pub-list-destacados" class="space-y-2 text-sm"></ul>
            <div class="grid gap-4 border-t border-slate-200/80 pt-4 sm:grid-cols-2">
              <div class="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                <p class="text-xs font-bold text-slate-800">Producto destacado 1</p>
                <label class="mt-2 block text-xs font-medium text-slate-600">URL del producto</label>
                <input id="pub-dest-1-url" type="text" autocomplete="off" class="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="#/p/… o enlace completo" />
                <label class="mt-2 block text-xs font-medium text-slate-600">Días activo (1–365)</label>
                <input id="pub-dest-1-dias" type="number" min="1" max="365" step="1" inputmode="numeric" class="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value="7" />
                <button type="button" id="pub-dest-1-publicar" class="mt-3 w-full rounded-xl bg-brand-primary px-4 py-2.5 text-sm font-bold text-white shadow-md transition hover:brightness-95">
                  Publicar destacado
                </button>
              </div>
              <div class="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                <p class="text-xs font-bold text-slate-800">Producto destacado 2</p>
                <label class="mt-2 block text-xs font-medium text-slate-600">URL del producto</label>
                <input id="pub-dest-2-url" type="text" autocomplete="off" class="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="#/p/… o enlace completo" />
                <label class="mt-2 block text-xs font-medium text-slate-600">Días activo (1–365)</label>
                <input id="pub-dest-2-dias" type="number" min="1" max="365" step="1" inputmode="numeric" class="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value="7" />
                <button type="button" id="pub-dest-2-publicar" class="mt-3 w-full rounded-xl bg-brand-primary px-4 py-2.5 text-sm font-bold text-white shadow-md transition hover:brightness-95">
                  Publicar destacado
                </button>
              </div>
            </div>
          </div>

          <div class="mt-6 space-y-3 rounded-xl border border-slate-200 bg-slate-50/80 p-4">
            <div class="flex flex-wrap items-center justify-between gap-2">
              <h3 class="font-semibold text-slate-900">Comercios del barrio</h3>
              <span id="pub-badge-com" class="shrink-0 rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-slate-600">—</span>
            </div>
            <p class="text-xs text-slate-600">Hasta 6 comercios en el inicio del catálogo. Identificás al vendedor por UID o código vecino.</p>
            <ul id="pub-list-comercios" class="space-y-2 text-sm"></ul>
            <div class="grid gap-3 border-t border-slate-200/80 pt-3 sm:grid-cols-2">
              <div class="sm:col-span-2">
                <label class="text-xs font-medium text-slate-600">Título</label>
                <input id="pub-com-titulo" type="text" class="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
              </div>
              <div class="sm:col-span-2">
                <label class="text-xs font-medium text-slate-600">Subtítulo</label>
                <input id="pub-com-sub" type="text" class="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
              </div>
              <div class="sm:col-span-2">
                <label class="text-xs font-medium text-slate-600">UID Firebase o código vecino</label>
                <div class="mt-1 flex items-stretch gap-2">
                  <input id="pub-com-uid" type="text" autocomplete="off" class="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="UID largo o tag de 4 chars" />
                  <button type="button" id="pub-com-uid-verificar" class="shrink-0 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-800 shadow-sm hover:bg-slate-50">
                    Verificar
                  </button>
                </div>
                <p id="pub-com-uid-hint" class="mt-1.5 min-h-[2.75rem] text-[11px] leading-snug text-slate-400" aria-live="polite"></p>
              </div>
              <div class="sm:col-span-2">
                <label class="text-xs font-medium text-slate-600">URL imagen (opcional)</label>
                <input id="pub-com-img" type="url" class="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
              </div>
              <div>
                <label class="text-xs font-medium text-slate-600">Días en el catálogo (1–365)</label>
                <input id="pub-com-dias" type="number" min="1" max="365" step="1" inputmode="numeric" class="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value="7" />
              </div>
              <div class="sm:col-span-2">
                <button type="button" id="pub-btn-guardar-comercio" class="w-full rounded-xl bg-brand-primary px-5 py-2.5 text-sm font-bold text-white shadow-md transition hover:brightness-95">
                  Guardar en catálogo
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>
    `);

    const msgG = document.getElementById('admin-msg-global');
    const suggestEl = document.getElementById('admin-uid-suggest');
    const searchInp = document.getElementById('admin-search-uid');
    const accountPanel = document.getElementById('admin-account-panel');

    function setMsgG(text, kind = 'neutral') {
      if (!msgG) return;
      msgG.textContent = text || '';
      msgG.className =
        kind === 'error'
          ? 'min-h-[1.25rem] text-sm text-red-700'
          : kind === 'ok'
            ? 'min-h-[1.25rem] text-sm text-green-700'
            : 'min-h-[1.25rem] text-sm text-slate-600';
    }

    function setMsgPub(text, kind = 'neutral') {
      const el = document.getElementById('admin-msg-pub');
      if (!el) return;
      el.textContent = text || '';
      el.className =
        kind === 'error'
          ? 'mt-2 min-h-[1.25rem] text-sm text-red-700'
          : kind === 'ok'
            ? 'mt-2 min-h-[1.25rem] text-sm text-green-700'
            : 'mt-2 min-h-[1.25rem] text-sm text-slate-600';
    }

    const hintBase = 'mt-1.5 min-h-[2.75rem] text-[11px] leading-snug';

    async function verificarUidPublicidadCampo(inputId, hintId) {
      const input = document.getElementById(inputId);
      const hint = document.getElementById(hintId);
      if (!input || !hint) return null;
      const raw = String(input.value || '').trim();
      if (!raw) {
        hint.textContent = '';
        hint.className = `${hintBase} text-slate-400`;
        return null;
      }
      hint.textContent = 'Consultando…';
      hint.className = `${hintBase} text-slate-500`;
      try {
        const r = await adminRepository.resolverUidParaPublicidad(raw);
        if (!r.ok) {
          hint.textContent = r.error || 'No se encontró el usuario.';
          hint.className = `${hintBase} text-red-700`;
          return null;
        }
        hint.textContent = `${r.nombre_publico || 'Vecino'} — UID ${r.uid.slice(0, 6)}…`;
        hint.className = `${hintBase} text-emerald-800`;
        return r;
      } catch (e) {
        hint.textContent = e?.message || 'No se pudo verificar.';
        hint.className = `${hintBase} text-red-700`;
        return null;
      }
    }

    const debounceConsultaNegUid = debounce(() => {
      void verificarUidPublicidadCampo('pub-neg-uid', 'pub-neg-uid-hint');
    }, 450);
    const debounceConsultaComUid = debounce(() => {
      void verificarUidPublicidadCampo('pub-com-uid', 'pub-com-uid-hint');
    }, 450);

    function uidSlotValido(u) {
      return RX_UID.test(String(u || '').trim());
    }

    function fmtFinVigencia(ms) {
      if (ms == null || !Number.isFinite(ms)) return '';
      return new Date(ms).toLocaleString('es-AR', { dateStyle: 'medium', timeStyle: 'short' });
    }

    function badgeClass(kind) {
      if (kind === 'ok') return 'shrink-0 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-emerald-900';
      if (kind === 'warn') return 'shrink-0 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-amber-900';
      return 'shrink-0 rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-slate-600';
    }

    function textoEstadoFilaAdmin(row, doc) {
      if (!row || !String(row.titulo || '').trim() || !uidSlotValido(row.vendedor_uid)) return '';
      const vig = slotPublicidadCatalogoVigente(row, doc);
      const ms = validoHastaSlotMs(row, doc);
      if (ms == null) return vig ? 'En catálogo (sin fecha fin)' : '';
      if (vig) return `Ocupado hasta ${fmtFinVigencia(ms)}`;
      return `Vencido (${fmtFinVigencia(ms)})`;
    }

    function docForBadges() {
      const base = pubDocSnapshot && typeof pubDocSnapshot === 'object' ? { ...pubDocSnapshot } : {};
      const titulo = String(document.getElementById('pub-neg-titulo')?.value || '').trim();
      const uid = String(document.getElementById('pub-neg-uid')?.value || '').trim();
      let negocio_dia = base.negocio_dia;
      if (titulo && uidSlotValido(uid)) {
        let dias = 7;
        try {
          dias = parseVigenciaDiasPublicidadHome(document.getElementById('pub-neg-dias')?.value);
        } catch {
          dias = 7;
        }
        const sub = String(document.getElementById('pub-neg-sub')?.value || '').trim();
        negocio_dia = {
          titulo,
          subtitulo: sub || null,
          descripcion: sub || null,
          vendedor_uid: uid,
          imagen_url: String(document.getElementById('pub-neg-img')?.value || '').trim() || null,
          valido_hasta: Timestamp.fromMillis(Date.now() + dias * 86400000),
        };
      } else if (!titulo) {
        negocio_dia = null;
      }
      return {
        ...base,
        negocio_dia,
        destacados_items: serializarDestacadosFirestore(),
        comercios_barrio: comerciosRows,
      };
    }

    function updateAllPubBadges(doc) {
      const d = doc && typeof doc === 'object' ? doc : {};
      const neg = d.negocio_dia && typeof d.negocio_dia === 'object' ? d.negocio_dia : null;
      const bn = document.getElementById('pub-badge-negocio');
      if (bn) {
        const tit = neg ? String(neg.titulo || '').trim() : '';
        const uid = neg ? String(neg.vendedor_uid || '').trim() : '';
        if (!tit || !uidSlotValido(uid)) {
          bn.textContent = 'Libre';
          bn.className = badgeClass('neutral');
        } else {
          const vig = slotPublicidadCatalogoVigente(neg, d);
          const ms = validoHastaSlotMs(neg, d);
          if (vig && ms != null) {
            bn.textContent = `Ocupado hasta ${fmtFinVigencia(ms)}`;
            bn.className = badgeClass('ok');
          } else if (!vig && ms != null) {
            bn.textContent = `Vencido · ${fmtFinVigencia(ms)}`;
            bn.className = badgeClass('warn');
          } else {
            bn.textContent = vig ? 'Ocupado (sin fecha)' : 'Libre';
            bn.className = badgeClass(vig ? 'ok' : 'neutral');
          }
        }
      }

      const destArr = Array.isArray(d.destacados_items) ? d.destacados_items : [];
      const nDestV = destArr.filter(
        (row) =>
          row &&
          String(row.titulo || '').trim() &&
          uidSlotValido(row.vendedor_uid) &&
          slotPublicidadCatalogoVigente(row, d)
      ).length;
      const bd = document.getElementById('pub-badge-dest');
      if (bd) {
        bd.textContent = nDestV === 0 ? 'Libre' : `Ocupado (${nDestV} en catálogo)`;
        bd.className = badgeClass(nDestV === 0 ? 'neutral' : 'ok');
      }

      const comArr = Array.isArray(d.comercios_barrio) ? d.comercios_barrio : [];
      const nComV = comArr.filter(
        (row) =>
          row &&
          String(row.titulo || '').trim() &&
          uidSlotValido(row.vendedor_uid) &&
          slotPublicidadCatalogoVigente(row, d)
      ).length;
      const bc = document.getElementById('pub-badge-com');
      if (bc) {
        bc.textContent = nComV === 0 ? 'Libre' : `Ocupado (${nComV} en catálogo)`;
        bc.className = badgeClass(nComV === 0 ? 'neutral' : 'ok');
      }
    }

    function refreshPubBadges() {
      updateAllPubBadges(docForBadges());
    }

    document.getElementById('admin-back').onclick = () => onBack?.();

    function hideSuggest() {
      if (!suggestEl) return;
      suggestEl.classList.add('hidden');
      suggestEl.innerHTML = '';
    }

    async function hydrateAdminServiciosRow() {
      const host = document.getElementById('admin-serv-tarjetas-host');
      const btn = document.getElementById('admin-serv-guardar-perfil');
      const selModo = document.getElementById('admin-serv-prof-modo');
      const inpSaldo = document.getElementById('admin-serv-saldo');
      if (!host || !selModo || !inpSaldo || !selected) return;
      host.innerHTML = '<p class="text-xs text-slate-500">Cargando perfil de servicios…</p>';
      try {
        const prof = await adminRepository.obtenerProfesionalServicios(selected.uid);
        if (!prof) {
          host.innerHTML =
            '<p class="text-sm text-slate-600">Sin documento en profesionales. El usuario debe abrir <strong class="font-medium">Mi perfil profesional</strong> en Servicios del barrio al menos una vez.</p>';
          if (btn) btn.disabled = true;
          selModo.disabled = true;
          inpSaldo.disabled = true;
          return;
        }
        if (btn) btn.disabled = false;
        selModo.disabled = false;
        inpSaldo.disabled = false;
        selModo.value = prof.perfil_validado === true ? 'verificado' : 'free';
        inpSaldo.value = String(Number(prof.tarjetas_saldo) || 0);
        const tarjetas = await adminRepository.listarTarjetasServiciosAdmin(selected.uid);
        if (!tarjetas.length) {
          host.innerHTML = '<p class="text-xs text-slate-500">No hay tarjetas publicadas.</p>';
          return;
        }
        host.innerHTML = tarjetas
          .map(
            (t) => `
        <div class="rounded-lg border border-slate-200 bg-white p-3 text-xs shadow-sm" data-admin-tid="${esc(t.id)}">
          <p class="font-mono text-[10px] text-slate-400">${esc(t.id)}</p>
          <label class="mt-2 block text-[11px] font-medium text-slate-600">Título</label>
          <input type="text" class="admin-tj-tit mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm" value="${esc(String(t.titulo_caso || ''))}" />
          <label class="mt-2 block text-[11px] font-medium text-slate-600">Cliente</label>
          <input type="text" class="admin-tj-cli mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm" value="${esc(String(t.cliente_display || ''))}" />
          <label class="mt-2 block text-[11px] font-medium text-slate-600">Duración</label>
          <input type="text" class="admin-tj-dur mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm" value="${esc(String(t.duracion_txt || ''))}" />
          <div class="mt-2 flex flex-wrap gap-4">
            <label class="flex items-center gap-2 text-[11px]"><input type="checkbox" class="admin-tj-vis h-4 w-4 rounded border-slate-300"${t.visible !== false ? ' checked' : ''} /> Visible</label>
            <label class="flex items-center gap-2 text-[11px]"><input type="checkbox" class="admin-tj-occ h-4 w-4 rounded border-slate-300"${t.oculta_por_admin === true ? ' checked' : ''} /> Oculta por moderación</label>
          </div>
          <button type="button" class="admin-tj-save mt-3 rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-semibold text-white hover:brightness-95">Guardar tarjeta</button>
        </div>`
          )
          .join('');
        host.querySelectorAll('.admin-tj-save').forEach((b) => {
          b.onclick = async () => {
            const card = b.closest('[data-admin-tid]');
            const tid = card?.getAttribute('data-admin-tid');
            if (!tid || !selected) return;
            b.disabled = true;
            try {
              await adminRepository.actualizarTarjetaServiciosAdmin(selected.uid, tid, {
                titulo_caso: card.querySelector('.admin-tj-tit')?.value || '',
                cliente_display: card.querySelector('.admin-tj-cli')?.value || '',
                duracion_txt: card.querySelector('.admin-tj-dur')?.value || '',
                visible: card.querySelector('.admin-tj-vis')?.checked === true,
                oculta_por_admin: card.querySelector('.admin-tj-occ')?.checked === true,
              });
              setMsgG('Tarjeta actualizada.', 'ok');
            } catch (err) {
              console.error(err);
              setMsgG(err?.message || 'No se pudo guardar la tarjeta.', 'error');
            } finally {
              b.disabled = false;
            }
          };
        });
      } catch (e) {
        console.error(e);
        host.innerHTML = `<p class="text-sm text-red-700">${esc(e?.message || 'Error')}</p>`;
      }
    }

    function fillAccountPanel(row) {
      if (!row || !accountPanel) return;
      accountPanel.classList.remove('hidden');
      document.getElementById('admin-acc-nombre').textContent = row.nombre || '—';
      const uidLabel = document.getElementById('admin-acc-uid');
      if (uidLabel) uidLabel.textContent = `UID ${row.uid}${row.user_tag ? ` · @${row.user_tag}` : ''}${row.email ? ` · ${row.email}` : ''}`;
      const estadoWrap = document.getElementById('admin-acc-estado-wrap');
      if (estadoWrap) estadoWrap.innerHTML = htmlEstadoCuentaAdmin(row);
      const selEstadoCuenta = document.getElementById('admin-acc-estado-cuenta');
      if (selEstadoCuenta) {
        selEstadoCuenta.value =
          String(row.estadoCuentaPrincipal || 'activa').toLowerCase() === 'suspendida'
            ? 'suspendida'
            : 'activa';
      }
      const btnGuardarEstado = document.getElementById('admin-acc-guardar-estado');
      if (btnGuardarEstado) {
        btnGuardarEstado.onclick = async () => {
          if (!selected) return;
          const val = String(document.getElementById('admin-acc-estado-cuenta')?.value || 'activa');
          btnGuardarEstado.disabled = true;
          setMsgG('Guardando estado de acceso…');
          try {
            await adminRepository.setEstadoCuentaPersona({
              uid: selected.uid,
              estado: val,
            });
            const fresh = await adminRepository.obtenerCuentaPorPersonaId(selected.uid);
            if (fresh) {
              selected = fresh;
              fillAccountPanel(fresh);
            }
            setMsgG(val === 'suspendida' ? 'Cuenta suspendida.' : 'Cuenta habilitada.', 'ok');
          } catch (e) {
            console.error(e);
            setMsgG(e?.message || 'No se pudo guardar el estado.', 'error');
          } finally {
            btnGuardarEstado.disabled = false;
          }
        };
      }
      const deudaInp = document.getElementById('admin-acc-deuda');
      const saldoInp = document.getElementById('admin-acc-saldo');
      const recInp = document.getElementById('admin-acc-monto-recibido');
      if (deudaInp) deudaInp.value = String(row.comision_deuda_ars ?? 0);
      if (saldoInp) saldoInp.value = String(row.saldo_favor_ars ?? 0);
      if (recInp) recInp.value = '';
      const sel = document.getElementById('admin-acc-plan');
      sel.value = row.tipoCuenta || 'free';

      const ph = document.getElementById('admin-acc-premium-hasta');
      if (ph) {
        const ts = row.premium_hasta_principal;
        if (!ts || typeof ts.toMillis !== 'function') {
          ph.textContent =
            'Vigencia en documento: sin tope (cuenta anterior). La próxima asignación Premium guardará fecha de fin.';
        } else {
          const ms = ts.toMillis();
          const fecha = new Date(ms).toLocaleString('es-AR', {
            dateStyle: 'medium',
            timeStyle: 'short',
          });
          ph.innerHTML =
            ms > Date.now()
              ? `Vigencia premium: hasta <strong class="text-slate-800">${esc(fecha)}</strong>.`
              : `<span class="font-medium text-amber-800">La fecha ya pasó (${esc(fecha)}). La app usa límites Free.</span>`;
        }
      }

      const btnRec = document.getElementById('admin-acc-save-recibido');
      if (btnRec) {
        btnRec.onclick = async () => {
          if (!selected) return;
          let monto;
          try {
            monto = parseMontoAbonoComisionAdmin(
              document.getElementById('admin-acc-monto-recibido')?.value
            );
          } catch (err) {
            setMsgG(err?.message || 'Monto inválido.', 'error');
            return;
          }
          btnRec.disabled = true;
          setMsgG('Imputando pago…');
          try {
            const r = await adminRepository.aplicarAbonoManualComision({
              uid: selected.uid,
              montoArs: monto,
            });
            const parts = [
              `Imputado $ ${fmtArs(monto)}`,
              r?.aDeuda != null ? `a deuda: $ ${fmtArs(r.aDeuda)}` : '',
            ];
            if (r?.excedente > 0) {
              parts.push(`saldo a favor +$ ${fmtArs(r.excedente)}`);
            }
            setMsgG(parts.filter(Boolean).join(' · ') + '.', 'ok');
            const fresh = await adminRepository.obtenerCuentaPorPersonaId(selected.uid);
            if (fresh) {
              selected = fresh;
              fillAccountPanel(fresh);
            }
          } catch (e) {
            console.error(e);
            setMsgG(e?.message || 'No se pudo imputar.', 'error');
          } finally {
            btnRec.disabled = false;
          }
        };
      }

      const btnMontos = document.getElementById('admin-acc-save-montos');
      if (btnMontos) {
        btnMontos.onclick = async () => {
          if (!selected) return;
          let deuda;
          let saldo;
          try {
            deuda = parseMontoAdminNoNegativo(document.getElementById('admin-acc-deuda')?.value);
            saldo = parseMontoAdminNoNegativo(document.getElementById('admin-acc-saldo')?.value);
          } catch (err) {
            setMsgG(err?.message || 'Montos inválidos.', 'error');
            return;
          }
          btnMontos.disabled = true;
          setMsgG('Guardando ajuste…');
          try {
            await adminRepository.aplicarAjusteManualComisionSaldo({
              uid: selected.uid,
              comision_deuda_ars: deuda,
              saldo_favor_ars: saldo,
            });
            const fresh = await adminRepository.obtenerCuentaPorPersonaId(selected.uid);
            if (fresh) {
              selected = fresh;
              fillAccountPanel(fresh);
            }
            setMsgG(`Ajuste directo: deuda $ ${fmtArs(deuda)} · saldo $ ${fmtArs(saldo)}`, 'ok');
          } catch (e) {
            console.error(e);
            setMsgG(e?.message || 'No se pudo guardar.', 'error');
          } finally {
            btnMontos.disabled = false;
          }
        };
      }
      void hydrateAdminServiciosRow();
    }

    async function onSelectUid(uid) {
      hideSuggest();
      searchInp.value = '';
      setMsgG('Cargando cuenta…');
      try {
        const row = await adminRepository.obtenerCuentaPorPersonaId(uid);
        if (!row) {
          setMsgG('No se encontró la cuenta.', 'error');
          return;
        }
        selected = row;
        fillAccountPanel(row);
        setMsgG('Cuenta cargada.');
      } catch (e) {
        console.error(e);
        setMsgG(e?.message || 'Error al cargar.', 'error');
      }
    }

    const runSearch = debounce(async () => {
      if (!searchInp || !suggestEl) return;
      const q = String(searchInp.value || '').trim();
      if (q.length < 2) {
        hideSuggest();
        return;
      }
      try {
        const rows = await adminRepository.buscarPersonasPorPrefijoDni(q);
        if (!rows.length) {
          suggestEl.classList.remove('hidden');
          suggestEl.innerHTML =
            '<li class="px-3 py-2 text-sm text-slate-500">Sin coincidencias</li>';
          return;
        }
        suggestEl.classList.remove('hidden');
        suggestEl.innerHTML = rows
          .map(
            (r) => `
          <li role="option">
            <button type="button" class="flex w-full flex-col items-start px-3 py-2.5 text-left text-sm hover:bg-slate-50" data-uid="${esc(r.uid)}">
              <span class="font-semibold text-slate-900">${esc(r.nombre || '—')}</span>
              <span class="text-xs text-slate-500">${esc(r.email || r.uid)}</span>
            </button>
          </li>`
          )
          .join('');
        suggestEl.querySelectorAll('[data-uid]').forEach((btn) => {
          btn.onclick = () => void onSelectUid(btn.getAttribute('data-uid'));
        });
      } catch (e) {
        console.error(e);
        hideSuggest();
      }
    }, 280);

    searchInp?.addEventListener('input', runSearch);
    searchInp?.addEventListener('focus', runSearch);
    document.addEventListener(
      'click',
      (e) => {
        if (!suggestEl || !searchInp) return;
        if (e.target === searchInp || suggestEl.contains(e.target)) return;
        hideSuggest();
      },
      true
    );

    document.getElementById('admin-acc-plan').onchange = async (ev) => {
      if (!selected) return;
      const tipo = ev.target.value;
      const sel = ev.target;
      sel.disabled = true;
      setMsgG('Guardando plan…');
      try {
        await adminRepository.asignarTipoPremiumCuenta({
          uid: selected.uid,
          tipo,
        });
        const row = await adminRepository.obtenerCuentaPorPersonaId(selected.uid);
        if (row) {
          selected = row;
          fillAccountPanel(row);
        }
        setMsgG('Plan actualizado.', 'ok');
      } catch (e) {
        console.error(e);
        setMsgG(e?.message || 'No se pudo guardar el plan.', 'error');
        sel.value = selected.tipoCuenta || 'free';
      } finally {
        sel.disabled = false;
      }
    };

    document.getElementById('admin-serv-guardar-perfil')?.addEventListener('click', async () => {
      if (!selected) return;
      const btn = document.getElementById('admin-serv-guardar-perfil');
      if (btn) btn.disabled = true;
      try {
        await adminRepository.guardarProfesionalServiciosAdmin(selected.uid, {
          perfil_validado: document.getElementById('admin-serv-prof-modo')?.value === 'verificado',
          tarjetas_saldo: document.getElementById('admin-serv-saldo')?.value,
        });
        setMsgG('Perfil de servicios actualizado.', 'ok');
        await hydrateAdminServiciosRow();
      } catch (e) {
        console.error(e);
        setMsgG(e?.message || 'No se pudo guardar el perfil de servicios.', 'error');
      } finally {
        if (btn) btn.disabled = false;
      }
    });

    function fillNegocioForm(neg) {
      const n = neg && typeof neg === 'object' ? neg : {};
      document.getElementById('pub-neg-titulo').value = String(n.titulo || '');
      document.getElementById('pub-neg-sub').value = String(n.subtitulo || n.descripcion || '');
      document.getElementById('pub-neg-uid').value = String(n.vendedor_uid || '');
      document.getElementById('pub-neg-img').value = String(n.imagen_url || '');
      const vd = Number(n.vigencia_dias);
      const di = document.getElementById('pub-neg-dias');
      if (di) {
        di.value =
          Number.isFinite(vd) && vd >= 1 && vd <= 365 ? String(Math.floor(vd)) : '7';
      }
    }

    function mapDestacadoRowFromSnap(row) {
      const pid = String(row.producto_id || '').trim();
      return {
        titulo: String(row.titulo || '').trim(),
        subtitulo: String(row.subtitulo || '').trim() || null,
        vendedor_uid: String(row.vendedor_uid || '').trim(),
        imagen_url: String(row.imagen_url || '').trim() || null,
        ...(pid ? { producto_id: pid } : {}),
        valido_hasta: row.valido_hasta,
        vigencia_dias: Number.isFinite(Number(row.vigencia_dias))
          ? Math.floor(Number(row.vigencia_dias))
          : undefined,
      };
    }

    function syncDestacadoInputsFromSlots() {
      for (let i = 0; i < 2; i++) {
        const u = document.getElementById(i === 0 ? 'pub-dest-1-url' : 'pub-dest-2-url');
        const di = document.getElementById(i === 0 ? 'pub-dest-1-dias' : 'pub-dest-2-dias');
        const row = destacadosSlots[i];
        if (u) u.value = row?.producto_id ? `#/p/${row.producto_id}` : '';
        if (di) {
          if (row?.vigencia_dias != null && Number.isFinite(row.vigencia_dias)) {
            const vd = Math.floor(row.vigencia_dias);
            di.value = vd >= 1 && vd <= 365 ? String(vd) : '7';
          } else if (!row) di.value = '7';
        }
      }
    }

    async function quitarDestacadoSlot(slotIndex) {
      destacadosSlots[slotIndex] = null;
      renderDestacadosList();
      try {
        await adminRepository.guardarPublicidadHome({
          destacados_items: serializarDestacadosFirestore(),
          ...patchLegacyPublicidadRoot(),
        });
        await hydratePublicidadLocalFromSnapshot(await adminRepository.obtenerPublicidadHome());
        setMsgPub(`Espacio ${slotIndex + 1} vaciado.`, 'ok');
      } catch (e) {
        console.error(e);
        setMsgPub(e?.message || 'No se pudo guardar.', 'error');
        try {
          await hydratePublicidadLocalFromSnapshot(await adminRepository.obtenerPublicidadHome());
        } catch (_e) {
          /* ignore */
        }
      }
    }

    function renderDestacadosList() {
      const ul = document.getElementById('pub-list-destacados');
      if (!ul) return;
      const docRef = docForBadges();
      const parts = [];
      for (let i = 0; i < 2; i++) {
        const row = destacadosSlots[i];
        if (!row) {
          parts.push(
            `<li class="rounded-lg border border-dashed border-slate-200 bg-white/60 px-3 py-2 text-sm text-slate-500">Espacio ${i + 1} — vacío</li>`
          );
        } else {
          const pid = String(row.producto_id || '').trim();
          const uid = String(row.vendedor_uid || '').trim();
          parts.push(`<li class="rounded-lg border border-slate-200 bg-white px-3 py-2">
          <div class="flex items-start justify-between gap-2">
            <div class="min-w-0 flex-1">
              <div class="flex flex-wrap items-center justify-between gap-2">
                <p class="font-semibold text-slate-900">${esc(String(row.titulo || ''))}</p>
                <span class="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-slate-500">${esc(textoEstadoFilaAdmin(row, docRef))}</span>
              </div>
              <p class="text-xs text-slate-600">${esc(String(row.subtitulo || ''))}</p>
              <p class="mt-1 font-mono text-[10px] text-slate-700">UID ${esc(uid.slice(0, 10))}…${pid ? ` · id ${esc(pid)}` : ''}</p>
            </div>
            <button type="button" data-rm-dest-slot="${i}" class="shrink-0 text-sm font-semibold text-red-600 hover:underline">Quitar</button>
          </div>
        </li>`);
        }
      }
      ul.innerHTML = parts.join('');
      ul.querySelectorAll('[data-rm-dest-slot]').forEach((btn) => {
        btn.onclick = async () => {
          const s = Number(btn.getAttribute('data-rm-dest-slot'));
          if (!Number.isFinite(s) || s < 0 || s > 1) return;
          btn.disabled = true;
          await quitarDestacadoSlot(s);
          btn.disabled = false;
        };
      });
      refreshPubBadges();
    }

    function renderComerciosList() {
      const ul = document.getElementById('pub-list-comercios');
      if (!ul) return;
      const docRef = docForBadges();
      if (!comerciosRows.length) {
        ul.innerHTML = '<li class="text-sm text-slate-500">Ninguno en la lista.</li>';
        refreshPubBadges();
        return;
      }
      ul.innerHTML = comerciosRows
        .map((row, idx) => {
          const tags = Array.isArray(row.etiquetas) ? row.etiquetas : [];
          const tagsHtml = tags
            .slice(0, 4)
            .map(
              (t) =>
                `<span class="mr-1 inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-slate-600">${esc(String(t))}</span>`
            )
            .join('');
          const uid = String(row.vendedor_uid || '').trim();
          return `
        <li class="rounded-lg border border-slate-200 bg-white px-3 py-2">
          <div class="flex items-start justify-between gap-2">
            <div class="min-w-0">
              <div class="flex flex-wrap items-center justify-between gap-2">
                <p class="font-semibold text-slate-900">${esc(String(row.titulo || ''))}</p>
                <span class="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-slate-500">${esc(textoEstadoFilaAdmin(row, docRef))}</span>
              </div>
              <p class="text-xs text-slate-600">${esc(String(row.subtitulo || ''))}</p>
              ${tagsHtml ? `<div class="mt-1.5">${tagsHtml}</div>` : ''}
              <p class="mt-1 font-mono text-[10px] text-slate-500">UID ${esc(uid.slice(0, 10))}…</p>
            </div>
            <button type="button" data-rm-com="${idx}" class="shrink-0 text-sm font-semibold text-red-600 hover:underline">Quitar</button>
          </div>
        </li>`;
        })
        .join('');
      ul.querySelectorAll('[data-rm-com]').forEach((btn) => {
        btn.onclick = async () => {
          const i = Number(btn.getAttribute('data-rm-com'));
          if (!Number.isFinite(i)) return;
          comerciosRows = comerciosRows.filter((_, j) => j !== i);
          renderComerciosList();
          btn.disabled = true;
          try {
            await adminRepository.guardarPublicidadHome({
              comercios_barrio: serializarComerciosFirestore(),
              ...patchLegacyPublicidadRoot(),
            });
            await hydratePublicidadLocalFromSnapshot(await adminRepository.obtenerPublicidadHome());
            setMsgPub('Comercio quitado del catálogo.', 'ok');
          } catch (e) {
            console.error(e);
            setMsgPub(e?.message || 'No se pudo guardar tras quitar.', 'error');
            try {
              await hydratePublicidadLocalFromSnapshot(await adminRepository.obtenerPublicidadHome());
            } catch (_e) {
              /* ignore */
            }
          } finally {
            btn.disabled = false;
          }
        };
      });
      refreshPubBadges();
    }

    function patchLegacyPublicidadRoot() {
      return {
        valido_hasta: deleteField(),
        vigencia_dias: deleteField(),
        productos_impulsados: deleteField(),
      };
    }

    function serializeDestacadoRowFirestore(r) {
      const o = {
        titulo: r.titulo,
        subtitulo: r.subtitulo || null,
        vendedor_uid: r.vendedor_uid,
        imagen_url: r.imagen_url || null,
        valido_hasta: r.valido_hasta,
      };
      if (r.producto_id) o.producto_id = String(r.producto_id);
      if (Number.isFinite(r.vigencia_dias)) o.vigencia_dias = Math.floor(r.vigencia_dias);
      return o;
    }

    function serializarDestacadosFirestore() {
      const s0 = destacadosSlots[0];
      const s1 = destacadosSlots[1];
      if (!s0 && !s1) return [];
      if (s0 && s1) return [serializeDestacadoRowFirestore(s0), serializeDestacadoRowFirestore(s1)];
      if (s0) return [serializeDestacadoRowFirestore(s0)];
      return [null, serializeDestacadoRowFirestore(s1)];
    }

    function serializarComerciosFirestore() {
      return comerciosRows
        .filter((r) => uidSlotValido(r.vendedor_uid))
        .map((r) => {
          const o = {
            titulo: r.titulo,
            subtitulo: r.subtitulo || null,
            vendedor_uid: r.vendedor_uid,
            imagen_url: r.imagen_url || null,
            etiquetas: Array.isArray(r.etiquetas) ? r.etiquetas : [],
            valido_hasta: r.valido_hasta,
          };
          if (Number.isFinite(r.vigencia_dias)) o.vigencia_dias = Math.floor(r.vigencia_dias);
          return o;
        });
    }

    async function hydratePublicidadLocalFromSnapshot(snap) {
      pubDocSnapshot = snap;
      fillNegocioForm(snap?.negocio_dia);
      const rawDest = Array.isArray(snap?.destacados_items) ? snap.destacados_items : [];
      destacadosSlots = [null, null];
      const rowOk = (row) =>
        row &&
        typeof row === 'object' &&
        String(row.titulo || '').trim() &&
        uidSlotValido(row.vendedor_uid);
      if (rawDest.length === 2 && rawDest[0] == null && rowOk(rawDest[1])) {
        destacadosSlots[1] = mapDestacadoRowFromSnap(rawDest[1]);
      } else {
        for (let i = 0; i < Math.min(2, rawDest.length); i++) {
          if (rowOk(rawDest[i])) destacadosSlots[i] = mapDestacadoRowFromSnap(rawDest[i]);
        }
      }
      syncDestacadoInputsFromSlots();
      comerciosRows = [...(snap?.comercios_barrio || [])]
        .filter((r) => r && typeof r === 'object' && String(r.titulo || '').trim() && uidSlotValido(r.vendedor_uid))
        .map((r) => ({
          titulo: r.titulo,
          subtitulo: r.subtitulo,
          vendedor_uid: String(r.vendedor_uid || '').trim(),
          imagen_url: r.imagen_url,
          etiquetas: Array.isArray(r.etiquetas) ? r.etiquetas : [],
          valido_hasta: r.valido_hasta,
          vigencia_dias: Number.isFinite(Number(r.vigencia_dias))
            ? Math.floor(Number(r.vigencia_dias))
            : undefined,
        }));
      renderDestacadosList();
      renderComerciosList();
      refreshPubBadges();
      queueMicrotask(() => {
        const v = String(document.getElementById('pub-neg-uid')?.value || '').trim();
        if (v) void verificarUidPublicidadCampo('pub-neg-uid', 'pub-neg-uid-hint');
      });
    }

    async function publicarDestacadoEnSlot(slotIndex) {
      const urlEl = document.getElementById(slotIndex === 0 ? 'pub-dest-1-url' : 'pub-dest-2-url');
      const diasEl = document.getElementById(slotIndex === 0 ? 'pub-dest-1-dias' : 'pub-dest-2-dias');
      const btn = document.getElementById(slotIndex === 0 ? 'pub-dest-1-publicar' : 'pub-dest-2-publicar');
      const raw = String(urlEl?.value || '').trim();
      const pid = parseMlvProductIdFromUrlOrRaw(raw);
      if (!pid) {
        setMsgPub('Ingresá una URL o ID de producto válido.', 'error');
        return;
      }
      let dias;
      try {
        dias = parseVigenciaDiasPublicidadHome(diasEl?.value);
      } catch (err) {
        setMsgPub(err?.message || 'Días activo inválidos.', 'error');
        return;
      }
      setMsgPub('Validando producto…');
      if (btn) btn.disabled = true;
      try {
        const prod = await adminRepository.obtenerProductoParaDestacadoPublicidad(pid);
        if (!prod.ok) {
          setMsgPub(prod.error || 'No se pudo leer el producto.', 'error');
          return;
        }
        const valido_hasta = Timestamp.fromMillis(Date.now() + dias * 86400000);
        destacadosSlots[slotIndex] = {
          titulo: prod.titulo,
          subtitulo: prod.subtitulo || null,
          vendedor_uid: prod.vendedor_uid,
          imagen_url: prod.imagen_url || null,
          producto_id: prod.producto_id,
          valido_hasta,
          vigencia_dias: dias,
        };
        await adminRepository.guardarPublicidadHome({
          destacados_items: serializarDestacadosFirestore(),
          ...patchLegacyPublicidadRoot(),
        });
        await hydratePublicidadLocalFromSnapshot(await adminRepository.obtenerPublicidadHome());
        setMsgPub(`Destacado ${slotIndex + 1} publicado.`, 'ok');
      } catch (e) {
        console.error(e);
        setMsgPub(e?.message || 'Error al publicar.', 'error');
      } finally {
        if (btn) btn.disabled = false;
      }
    }

    document.getElementById('pub-dest-1-publicar')?.addEventListener('click', () => publicarDestacadoEnSlot(0));
    document.getElementById('pub-dest-2-publicar')?.addEventListener('click', () => publicarDestacadoEnSlot(1));

    document.getElementById('pub-neg-uid-verificar')?.addEventListener('click', () => {
      void verificarUidPublicidadCampo('pub-neg-uid', 'pub-neg-uid-hint');
    });
    document.getElementById('pub-com-uid-verificar')?.addEventListener('click', () => {
      void verificarUidPublicidadCampo('pub-com-uid', 'pub-com-uid-hint');
    });
    document.getElementById('pub-neg-uid')?.addEventListener('input', () => {
      refreshPubBadges();
      debounceConsultaNegUid();
    });
    document.getElementById('pub-com-uid')?.addEventListener('input', () => {
      debounceConsultaComUid();
    });

    document.getElementById('pub-btn-guardar-comercio')?.addEventListener('click', async () => {
      const btn = document.getElementById('pub-btn-guardar-comercio');
      const titulo = String(document.getElementById('pub-com-titulo')?.value || '').trim();
      if (!titulo) {
        setMsgPub('El título del comercio es obligatorio.', 'error');
        return;
      }
      if (comerciosRows.length >= 6) {
        setMsgPub('Ya hay 6 comercios (máximo). Quitá uno para agregar otro.', 'error');
        return;
      }
      const rawUid = String(document.getElementById('pub-com-uid')?.value || '').trim();
      if (btn) btn.disabled = true;
      setMsgPub('Guardando comercio…');
      let pushedRow = false;
      try {
        const v = await adminRepository.resolverUidParaPublicidad(rawUid);
        if (!v.ok) {
          setMsgPub(v.error || 'No se pudo resolver el vendedor.', 'error');
          return;
        }
        let diasCom;
        try {
          diasCom = parseVigenciaDiasPublicidadHome(document.getElementById('pub-com-dias')?.value);
        } catch (err) {
          setMsgPub(err?.message || 'Días del comercio inválidos.', 'error');
          return;
        }
        const sub = String(document.getElementById('pub-com-sub')?.value || '').trim();
        const newRow = {
          titulo,
          subtitulo: sub || null,
          vendedor_uid: v.uid,
          imagen_url: String(document.getElementById('pub-com-img')?.value || '').trim() || null,
          etiquetas: [],
          valido_hasta: Timestamp.fromMillis(Date.now() + diasCom * 86400000),
          vigencia_dias: diasCom,
        };
        comerciosRows.push(newRow);
        pushedRow = true;
        await adminRepository.guardarPublicidadHome({
          comercios_barrio: serializarComerciosFirestore(),
          ...patchLegacyPublicidadRoot(),
        });
        pushedRow = false;
        await hydratePublicidadLocalFromSnapshot(await adminRepository.obtenerPublicidadHome());
        document.getElementById('pub-com-titulo').value = '';
        document.getElementById('pub-com-sub').value = '';
        document.getElementById('pub-com-uid').value = '';
        document.getElementById('pub-com-img').value = '';
        const comHint = document.getElementById('pub-com-uid-hint');
        if (comHint) {
          comHint.textContent = '';
          comHint.className = `${hintBase} text-slate-400`;
        }
        renderComerciosList();
        setMsgPub('Comercio guardado en el catálogo.', 'ok');
      } catch (e) {
        console.error(e);
        if (pushedRow) comerciosRows.pop();
        setMsgPub(e?.message || 'Error al guardar el comercio.', 'error');
        renderComerciosList();
      } finally {
        if (btn) btn.disabled = false;
      }
    });

    document.getElementById('pub-btn-publicar-negocio')?.addEventListener('click', async () => {
      const btn = document.getElementById('pub-btn-publicar-negocio');
      setMsgPub('Publicando…');
      if (btn) btn.disabled = true;
      try {
        const titulo = String(document.getElementById('pub-neg-titulo')?.value || '').trim();
        let negocio_dia = null;
        if (titulo) {
          const rawUid = String(document.getElementById('pub-neg-uid')?.value || '').trim();
          const v = await adminRepository.resolverUidParaPublicidad(rawUid);
          if (!v.ok) {
            setMsgPub(v.error || 'No se pudo resolver el vendedor del negocio principal.', 'error');
            return;
          }
          let diasNeg;
          try {
            diasNeg = parseVigenciaDiasPublicidadHome(document.getElementById('pub-neg-dias')?.value);
          } catch (e) {
            setMsgPub(e?.message || 'Días del negocio principal inválidos.', 'error');
            return;
          }
          negocio_dia = {
            titulo,
            subtitulo: String(document.getElementById('pub-neg-sub')?.value || '').trim() || null,
            descripcion: String(document.getElementById('pub-neg-sub')?.value || '').trim() || null,
            imagen_url: String(document.getElementById('pub-neg-img')?.value || '').trim() || null,
            vendedor_uid: v.uid,
            valido_hasta: Timestamp.fromMillis(Date.now() + diasNeg * 86400000),
            vigencia_dias: diasNeg,
          };
        }
        await adminRepository.guardarPublicidadHome({
          negocio_dia: negocio_dia != null ? negocio_dia : deleteField(),
          ...patchLegacyPublicidadRoot(),
        });
        await hydratePublicidadLocalFromSnapshot(await adminRepository.obtenerPublicidadHome());
        setMsgPub('Negocio principal publicado.', 'ok');
      } catch (e) {
        console.error(e);
        setMsgPub(e?.message || 'Error al publicar.', 'error');
      } finally {
        if (btn) btn.disabled = false;
      }
    });

    async function hydrateInboxModeracion() {
      const ul = document.getElementById('admin-inbox-list');
      if (!ul) return;
      ul.innerHTML = '<li class="text-xs text-slate-500">Cargando…</li>';
      try {
        const rows = await adminRepository.listarAdminInboxModeracion(35);
        if (!rows.length) {
          ul.innerHTML = '<li class="text-xs text-slate-500">Sin alertas recientes.</li>';
          return;
        }
        ul.innerHTML = rows
          .map((r) => {
            const msg = esc(String(r.mensaje || r.tipo || ''));
            const t =
              r.creadoEn && typeof r.creadoEn.toMillis === 'function'
                ? new Date(r.creadoEn.toMillis()).toLocaleString('es-AR')
                : '—';
            return `<li class="rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-sm"><p class="text-slate-800">${msg}</p><p class="mt-1 text-[10px] text-slate-400">${esc(t)}</p></li>`;
          })
          .join('');
      } catch (e) {
        console.error(e);
        ul.innerHTML = `<li class="text-sm text-red-700">${esc(e?.message || 'No se pudo cargar el inbox.')}</li>`;
      }
    }

    document.getElementById('admin-inbox-refresh')?.addEventListener('click', () => void hydrateInboxModeracion());
    void hydrateInboxModeracion();

    try {
      await hydratePublicidadLocalFromSnapshot(await adminRepository.obtenerPublicidadHome());
    } catch (e) {
      console.error(e);
      setMsgPub('No se pudo cargar publicidad_home (¿reglas deployadas?).', 'error');
      renderDestacadosList();
      renderComerciosList();
      refreshPubBadges();
    }

    ['pub-neg-titulo', 'pub-neg-dias', 'pub-neg-sub', 'pub-neg-img', 'pub-dest-1-url', 'pub-dest-1-dias', 'pub-dest-2-url', 'pub-dest-2-dias'].forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      el.addEventListener('input', () => refreshPubBadges());
      el.addEventListener('change', () => refreshPubBadges());
    });
  }

  return { showPanelAdmin };
}
